import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Icon } from "./Icon.js";
import { useTexts } from "./texts.js";
import { cx, errorInfo } from "./util.js";
const Ctx = createContext(null);
export function useToast() {
    const c = useContext(Ctx);
    if (!c)
        throw new Error("ToastProvider missing");
    return c;
}
const icon = { success: "check", error: "octagon", info: "info" };
const accent = {
    success: "text-ok-fg bg-ok-subtle",
    error: "text-err-fg bg-err-subtle",
    info: "text-info-fg bg-info-subtle",
};
export function ToastProvider({ children }) {
    const texts = useTexts();
    const [items, setItems] = useState([]);
    const seq = useRef(0);
    const dismiss = useCallback((id) => setItems((l) => l.filter((t) => t.id !== id)), []);
    const push = useCallback((t) => {
        const id = ++seq.current;
        setItems((l) => [...l.slice(-3), { ...t, id }]);
        window.setTimeout(() => dismiss(id), t.kind === "error" ? 9000 : 4500);
    }, [dismiss]);
    const api = useMemo(() => ({
        success: (title, detail, action) => push({ kind: "success", title, detail, action }),
        info: (title, detail, action) => push({ kind: "info", title, detail, action }),
        error: (err, title) => {
            const { message: msg, requestId } = errorInfo(err);
            push({
                kind: "error",
                title: title ?? msg,
                detail: [title ? msg : "", requestId ? `${texts.requestId} ${requestId}` : ""].filter(Boolean).join(" · ") || undefined,
            });
        },
    }), [push, texts.requestId]);
    return (_jsxs(Ctx.Provider, { value: api, children: [children, _jsx("div", { "aria-live": "polite", role: "region", "aria-label": texts.notifications, className: "pointer-events-none fixed bottom-4 left-1/2 z-[80] flex w-[min(440px,calc(100vw-2rem))] -translate-x-1/2 flex-col gap-2", children: items.map((t) => (_jsxs("div", { role: t.kind === "error" ? "alert" : "status", className: "anim-pop pointer-events-auto flex items-start gap-3 rounded-2xl bg-elevated px-3.5 py-3 text-sm text-fg shadow-xl ring-1 ring-line", children: [_jsx("span", { className: cx("mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full", accent[t.kind]), children: _jsx(Icon, { name: icon[t.kind], size: 13, strokeWidth: 2.5 }) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "font-medium", children: t.title }), t.detail && _jsx("p", { className: "mt-0.5 break-words text-xs text-muted", children: t.detail })] }), t.action && (_jsx("button", { type: "button", className: "shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-accent hover:bg-hover dark:text-accent-on-dark", onClick: () => {
                                t.action.run();
                                dismiss(t.id);
                            }, children: t.action.label })), _jsx("button", { type: "button", "aria-label": texts.close, className: "shrink-0 rounded p-0.5 text-muted hover:text-fg", onClick: () => dismiss(t.id), children: _jsx(Icon, { name: "x", size: 14 }) })] }, t.id))) })] }));
}
